import React, { useCallback, useEffect, useRef } from "react";
import QuickPinchZoom, { make3dTransformValue } from "react-quick-pinch-zoom";

export enum ZoomDirection {
    in = "in",
    out = "out",
}
export interface ZoomableSlideProps {
    index: number;
    currentSlideIndex: number;
    onZoom: (direction: ZoomDirection) => void;
    onClick: () => void;
    children: React.ReactNode;
}

export function ZoomableSlide({
    index,
    currentSlideIndex,
    onZoom,
    onClick,
    children,
}: ZoomableSlideProps): React.ReactElement {
    const containerRef = useRef<HTMLDivElement>(null);
    const quickPinchZoomRef = useRef<QuickPinchZoom>(null);
    const isZoomedIn = useRef(false);
    const prevScale = useRef(1);
    const tapTimeoutRef = useRef<number | null>(null);

    const cancelPendingTap = useCallback(() => {
        if (tapTimeoutRef.current !== null) {
            window.clearTimeout(tapTimeoutRef.current);
            tapTimeoutRef.current = null;
        }
    }, []);

    const handleClick = useCallback(() => {
        if (tapTimeoutRef.current !== null) {
            // Second click within the double-tap window — part of a double-tap-to-zoom.
            cancelPendingTap();
            return;
        }
        tapTimeoutRef.current = window.setTimeout(() => {
            tapTimeoutRef.current = null;
            onClick();
        }, 300);
    }, [cancelPendingTap, onClick]);

    const resetZoom = useCallback(() => {
        const wrapper = containerRef.current;
        const currentZoom = quickPinchZoomRef.current;
        if (wrapper && currentZoom) {
            const { width, height } = wrapper.getBoundingClientRect();
            // reset inactive slide back to centre
            currentZoom.alignCenter({ x: width / 2, y: height / 2, scale: 1 });
            isZoomedIn.current = false;
            prevScale.current = 1;
        }
    }, []);

    const onUpdate = useCallback(
        ({ x, y, scale }: { x: number; y: number; scale: number }) => {
            const wrapper = containerRef.current;
            if (wrapper) {
                wrapper.style.setProperty("transform", make3dTransformValue({ x, y, scale }));
                const direction =
                    scale > prevScale.current
                        ? ZoomDirection.in
                        : scale < prevScale.current
                        ? ZoomDirection.out
                        : null;

                if (direction === ZoomDirection.in && !isZoomedIn.current) {
                    isZoomedIn.current = true;
                    onZoom(ZoomDirection.in);
                } else if (direction === ZoomDirection.out && isZoomedIn.current) {
                    isZoomedIn.current = false;
                    onZoom(ZoomDirection.out);
                }
                prevScale.current = scale;
            }
        },
        [onZoom]
    );

    useEffect(() => {
        if (index !== currentSlideIndex) {
            cancelPendingTap();
            resetZoom();
        }
    }, [currentSlideIndex, index, cancelPendingTap, resetZoom]);

    useEffect(() => cancelPendingTap, [cancelPendingTap]);

    return (
        <QuickPinchZoom
            ref={quickPinchZoomRef}
            onUpdate={onUpdate}
            onDoubleTap={cancelPendingTap}
            draggableUnZoomed={false}
            maxZoom={3}
            doubleTapZoomOutOnMaxScale
            inertia={false}
        >
            <div ref={containerRef} onClick={handleClick} role="none">
                {children}
            </div>
        </QuickPinchZoom>
    );
}
